/**
 * Scene — "headings collide → window → step through" scroll sequence.
 *
 * Plain script (no bundler). Loaded with `defer` from the section, which sits
 * earlier in the document than the GSAP libraries at the end of <body>; defer
 * scripts run in document order, so we wait for GSAP to be ready before init.
 *
 * Model (same portal mechanic as the Intro Portal): the image is full-screen
 * behind a veil in the room colour; the veil has an arch-shaped hole (CSS mask)
 * so at rest the visitor sees the image through a "window". One pinned, scrubbed
 * ScrollTrigger drives four beats:
 *   1. Two headings slide in from opposite sides and settle centred, stacked.
 *   2. The window opens (hole 0 → rest) and the image fades in; the flanking
 *      tag + button pairs and the body line rise into place.
 *   3. A brief pause.
 *   4. "Step through": the hole grows to full screen, the image scales a touch,
 *      the veil + all overlays fade — leaving the same image full-screen.
 *
 * Respects prefers-reduced-motion: no pin/scrub — the composed rest state is
 * left as the CSS renders it. Re-initialises cleanly on editor section reloads.
 */
(function () {
  var triggers = []; // ScrollTriggers we create (for editor teardown)
  var tweens = []; // timelines/tweens we create
  var cleanups = []; // misc listener removers

  function ready() {
    return window.gsap && window.ScrollTrigger;
  }

  function whenReady(cb) {
    if (ready()) return cb();
    // Poll until the GSAP libs (later in the document) have executed, so init
    // can never silently no-op if they aren't ready at DOMContentLoaded.
    var tries = 0;
    (function poll() {
      if (ready()) return cb();
      if (tries++ > 600) return; // ~10s safety cap
      requestAnimationFrame(poll);
    })();
  }

  function finalHole() {
    return Math.max(window.innerWidth, window.innerHeight) * 1.7;
  }

  // NOTE: "portal transition" link handling (data-portal-link → exit-overlay fade
  // → navigate) now lives in the global assets/portal-link.js, loaded on every
  // page from theme.liquid — the header uses it too, and the header is site-wide.

  function reset() {
    triggers.forEach(function (t) {
      t.kill();
    });
    tweens.forEach(function (t) {
      t.kill();
    });
    cleanups.forEach(function (fn) {
      fn();
    });
    triggers = [];
    tweens = [];
    cleanups = [];
    document.querySelectorAll('[data-scene]').forEach(function (s) {
      s.__sceneInit = false;
    });
  }

  // gsap.to() throws on a null target — filter optional content out.
  function present(list) {
    return list.filter(function (el) {
      return !!el;
    });
  }

  function setup(section) {
    if (!section || section.__sceneInit) return;
    section.__sceneInit = true;

    // Blend this section's bottom into whatever follows: read the next section's
    // background colour (exposed as data-ui-bg) and fade toward it, so the
    // dissolve never meets a hard colour edge. Falls back to the flat --scene-bg.
    var wrap = section.closest('.shopify-section');
    var nextEl = wrap && wrap.nextElementSibling;
    var nextColored =
      nextEl && (nextEl.matches('[data-ui-bg]') ? nextEl : nextEl.querySelector('[data-ui-bg]'));
    var nextBg = nextColored && nextColored.getAttribute('data-ui-bg');
    if (nextBg) section.style.setProperty('--scene-fade-to', nextBg);

    var bg = section.querySelector('[data-bg]');
    var fade = section.querySelector('[data-fade]'); // next-section colour backdrop
    var veil = section.querySelector('[data-veil]');
    var hScript = section.querySelector('[data-h-script]');
    var hSans = section.querySelector('[data-h-sans]');
    var slot = section.querySelector('[data-window]');
    var asides = present([].slice.call(section.querySelectorAll('[data-aside]')));
    var body = section.querySelector('[data-body]');

    // Rest hole = the window slot's rendered width, so the masked arch lines up
    // exactly with the transparent slot. Function-based so it re-evaluates on
    // refresh (the slot is sized in svh and changes with the viewport).
    function restHole() {
      return slot ? slot.getBoundingClientRect().width : 0;
    }

    // "Static mode": no pin/scrub. True under reduced-motion AND inside the
    // Shopify theme editor (Shopify.designMode), so editing shows the composed
    // rest state (window open, all content visible) instead of a scrubbed pin.
    var staticMode =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !!(window.Shopify && window.Shopify.designMode);
    if (staticMode) return;

    // Initial (hidden / closed) state. Body is horizontally centred with a CSS
    // translateX(-50%); express that as xPercent so animating y composes on top.
    // The image "bloom" (blur → focus) is desktop-only — animated full-screen
    // blur is GPU-heavy on phones, so small screens get a plain fade instead.
    // Blur is only ever applied under motion (this whole block is past the
    // staticMode early-return), so reduced-motion/editor stay sharp.
    var allowBlur = window.matchMedia('(min-width: 64rem)').matches;
    if (bg) bg.classList.remove('is-evaporating'); // clear any stale state from an editor reload
    gsap.set(veil, { '--hole': '0px' });
    if (allowBlur) gsap.set(bg, { opacity: 0, scale: 1, filter: 'blur(14px)' });
    else gsap.set(bg, { opacity: 0, scale: 1 });
    // Text + buttons also focus in from a soft blur (blur(6px) → 0) as they rise,
    // matching the Interlude + Survey reveals so the sections feel cohesive. Small
    // elements only (not the full-screen image), so cheap enough to run on mobile
    // too; still gated behind the staticMode return, so reduced-motion stays sharp.
    if (hScript) gsap.set(hScript, { x: -40, autoAlpha: 0, filter: 'blur(6px)' });
    if (hSans) gsap.set(hSans, { x: 40, autoAlpha: 0, filter: 'blur(6px)' });
    if (asides.length) gsap.set(asides, { y: 24, autoAlpha: 0, filter: 'blur(6px)' });
    if (body) gsap.set(body, { xPercent: -50, x: 0, y: 24, autoAlpha: 0, filter: 'blur(6px)' });

    // --- Approach reveal: the WHOLE composition develops DURING the scroll-up,
    // from the section entering (top bottom) to it filling the screen (top top),
    // reusing what used to be empty leftover scroll from the intro. As you scroll
    // the section into place the headings arrive, the window opens, the image
    // blooms into focus and the tags + body rise — a top-to-bottom cascade that
    // lands exactly as the section reaches full screen. Non-pinned + scrubbed so
    // it's tied to scroll and reverses cleanly; the pinned timeline below then
    // starts from this composed state and only steps through + evaporates.
    var approach = gsap.timeline({
      defaults: { ease: 'power2.out' },
      scrollTrigger: {
        trigger: section,
        start: 'top bottom', // the intro photo has just finished fading here
        end: 'top top', // everything landed as the section fills the screen
        scrub: 1,
        invalidateOnRefresh: true,
        // Match the pin's priority so it refreshes AFTER the intro's dynamic
        // pin-spacer is applied and measures its start/end correctly.
        refreshPriority: -1
      }
    });
    tweens.push(approach);
    if (approach.scrollTrigger) triggers.push(approach.scrollTrigger);

    // Headings first (they sit highest, so they scroll into view first).
    if (hScript) approach.to(hScript, { x: 0, autoAlpha: 1, filter: 'blur(0px)', duration: 2.4 }, 0);
    if (hSans) approach.to(hSans, { x: 0, autoAlpha: 1, filter: 'blur(0px)', duration: 2.4 }, 0);
    // Window opens + image blooms into focus (desktop) as it rises into view.
    // Offsets pulled early so the whole reveal front-loads — it begins as soon as
    // the section starts entering, not a beat later.
    approach.to(veil, { '--hole': function () { return restHole() + 'px'; }, duration: 2.4 }, 1.0);
    approach.to(bg, { opacity: 1, duration: 2.4 }, 1.0);
    if (allowBlur) approach.to(bg, { filter: 'blur(0px)', ease: 'power2.out', duration: 2.8 }, 1.0);
    // Tags then body rise last, landing right as the section fills the screen.
    if (asides.length) approach.to(asides, { y: 0, autoAlpha: 1, filter: 'blur(0px)', duration: 2.2 }, 1.4);
    if (body) approach.to(body, { y: 0, autoAlpha: 1, filter: 'blur(0px)', duration: 2.2 }, 1.6);

    // Mobile only: an empty settle tail after the last reveal. The body is the LAST
    // tween here (ends at 3.8), so under scrub lag it's still climbing to autoAlpha:1
    // at the approach→pin handoff while the pin begins fading the overlays out — the
    // tug leaves the body lingering, so it disappears a beat AFTER the headings +
    // buttons. Extending the timeline lets the body's reveal complete at ~83% of the
    // scroll range, resolving its lag before the fade so every overlay is fully
    // settled at the handoff and the shared fade-out (below) reads simultaneous.
    // Desktop keeps its tuned pacing (no such complaint). Lever: bump 0.8 for more
    // settle / tighter sync (at a slightly faster mobile reveal). Reverse re-reveal
    // and the pin fade are untouched — this only adds empty time to the tail.
    if (!allowBlur) approach.to({}, { duration: 0.8 }, 3.8);

    // Overlays revealed by the approach timeline; the pin fades them out on step-
    // through (beat 4 below). Defined here so the pin's ScrollTrigger can also
    // re-assert their hidden state — see enforceOverlayHide.
    var overlays = present([hScript, hSans, body].concat(asides));

    // The overlays are owned by TWO scrubbed timelines: the 'approach' reveal holds
    // them visible (autoAlpha:1) at its end, and this pin fades them out. Any
    // ScrollTrigger.refresh() (late fonts/images, resize) re-renders the approach's
    // stale visible state, which can flash back over the stepped-through image —
    // most visibly the centred body. Once we're past the step-through fade (tl time
    // ~0.8) re-assert hidden on every update + refresh so it can never reappear.
    // Below that (entering the pin / scrolling back up) this no-ops, leaving the
    // scrubbed fade + reverse re-reveal exactly as they were — no visual change.
    function enforceOverlayHide(self) {
      if (overlays.length && self.animation && self.progress * self.animation.duration() > 1) {
        gsap.set(overlays, { autoAlpha: 0 });
      }
    }

    var tl = gsap.timeline({
      defaults: { ease: 'power2.out' },
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        // The pin now covers ONLY the entrance (hold + window step-through) — the
        // image dissolve was moved off the pin onto the natural scroll-off (see the
        // `evap` timeline below), so the gold Interlude can rise in DURING the fade
        // instead of after a long pinned distance. Mobile is tightened further (the
        // step-through is shorter there too — see holdDur/stepDur).
        end: allowBlur ? '+=190%' : '+=105%',
        scrub: 1, // smoothing: the words ease in with scroll momentum, not 1:1
        pin: true,
        pinSpacing: true,
        invalidateOnRefresh: true,
        // A flick that outruns the scrub snaps the step-through to its end instead
        // of scrubbing through a long catch-up — kills the "stuck mid-transition"
        // feel on a fast scroll. No effect on slow (luxurious) scrolling.
        fastScrollEnd: true,
        onUpdate: enforceOverlayHide,
        onRefresh: enforceOverlayHide,
        // This section sits below the Intro Portal's (also pinned) dissolve.
        // A lower priority makes it refresh LAST, after the intro's pin-spacer
        // is applied, so its pin start is measured correctly (no snap).
        refreshPriority: -1
      }
    });
    tweens.push(tl);
    if (tl.scrollTrigger) triggers.push(tl.scrollTrigger);

    // The pin's own onRefresh can run BEFORE the approach re-renders its visible
    // end-state during a global _refreshAll (both triggers are refreshPriority:-1,
    // order non-deterministic), so the per-trigger guard alone can lose the race and
    // let the centred body flash back over the stepped-through image — visible on
    // mobile Safari where address-bar/viewport changes cause extra refreshes. The
    // global "refresh" event fires AFTER the whole refresh cycle, so re-asserting the
    // hidden state here wins deterministically. Only listens (no refresh() call), so
    // it doesn't trip the bare-refresh hazard noted at the bottom of setup.
    function onGlobalRefresh() {
      if (tl.scrollTrigger) enforceOverlayHide(tl.scrollTrigger);
    }
    ScrollTrigger.addEventListener('refresh', onGlobalRefresh);
    cleanups.push(function () {
      ScrollTrigger.removeEventListener('refresh', onGlobalRefresh);
    });

    // NOTE: the entire reveal (headings, window, image, tags, body) happens in the
    // approach timeline above, so the scene is fully composed when this pin starts.
    // This PINNED timeline only holds + steps through the window (the entrance). The
    // image dissolve (old "beat 5") now lives in the NON-pinned `evap` timeline below,
    // so it rides the natural scroll-off with the Interlude rising in behind it.

    // Mobile is tightened: shorter hold + a quicker window-open (the client kept the
    // desktop pace and asked to tighten only mobile). The overlay + veil fades derive
    // their positions from these so they stay locked to the step-through on both.
    var holdDur = allowBlur ? 0.8 : 0.5;
    var stepDur = allowBlur ? 4 : 2.5;
    var dwellDur = allowBlur ? 0.6 : 0.4; // beat 4b: land on the full image before it goes
    var stepAt = holdDur; // step-through starts right after the hold
    var stepEnd = stepAt + stepDur;

    // --- Beat 3: brief hold on the composed scene before stepping through. ---
    tl.to({}, { duration: holdDur }, 0);

    // --- Beat 4: step through the window → same image, full screen. ------
    // Explicit `from` (immediateRender:false so it only applies when the playhead
    // reaches it, not at build) — the pin OWNS its composed start-state instead of
    // capturing whatever the scrubbed `approach` left in the DOM. On a fast flick
    // the approach can still be mid-lerp (window part-open) when the pin engages;
    // a bare .to() would bake that half-open value as its `from` and pop on reverse.
    // restHole()/finalHole() stay function-based so they re-measure on refresh.
    tl.fromTo(
      veil,
      { '--hole': function () { return restHole() + 'px'; } },
      { '--hole': function () { return finalHole() + 'px'; }, ease: 'power1.inOut', duration: stepDur, immediateRender: false },
      stepAt
    );
    // Desktop only: the subtle scale-up is GPU-heavy on a masked full-screen layer
    // and, on mobile Safari, contributed to the image dropping/popping mid-scroll.
    // Mobile holds scale: 1 through the step-through. (The evaporate below picks the
    // image up from scale 1.08 on desktop / scale 1 on mobile — continuity preserved.)
    if (allowBlur) tl.fromTo(bg, { scale: 1 }, { scale: 1.08, ease: 'none', duration: stepDur, immediateRender: false }, stepAt);
    // Fade the overlays out DURING the hold so they're fully gone the instant the
    // window starts opening (at stepAt). If this trailed into the hole growth, the
    // image would already fill the screen width (~30% open) while the body copy was
    // still fading — visible as body-over-image, especially on a fast (scrub-lagged)
    // scroll. Must complete by stepAt. (enforceOverlayHide then keeps them hidden past
    // the fade so a refresh can't re-render the approach's visible state on top.)
    if (overlays.length) tl.to(overlays, { autoAlpha: 0, duration: Math.max(0.3, holdDur - 0.2) }, 0.2);
    // Fade the veil out near the end of the step-through so the arch's concave edges
    // don't leave coloured wedges. Anchored to stepEnd so it tracks either pace.
    tl.to(veil, { autoAlpha: 0, duration: 1.2 }, stepEnd - 1.4);

    // --- Beat 4b: DWELL. Hold the fully-arrived image — locked full-screen and sharp
    // — for a beat before anything starts to fade. The client wants people to fully
    // land in the image before it goes away, so the dissolve (evap, below) is kept
    // entirely off the pin and can't begin until this hold releases. Empty tween =
    // pinned pause; the pin `end` includes it so the step-through pace is unchanged.
    tl.to({}, { duration: dwellDur }, stepEnd);

    // Feather the image's bottom edge only while it's dissolving (mobile CSS acts on
    // .is-evaporating; harmless on desktop, which the media query excludes). Gated to
    // !allowBlur so desktop never touches the DOM here.
    function featherOn() {
      if (!allowBlur && bg) bg.classList.add('is-evaporating');
    }
    function featherOff() {
      if (bg) bg.classList.remove('is-evaporating');
    }

    // --- Exit: the revealed image EVAPORATES as the Scene scrolls off. Anchored to the
    // NEXT section (the Interlude), NOT the Scene: the Scene is pinned, so a trigger on
    // it measures 'bottom …' against the pinned/fixed box and resolves DURING the pin —
    // which made the image fade while the window was still opening, then finish long
    // before the Interlude arrived (a whole screen of empty scroll after it was gone).
    // The Interlude isn't pinned, so 'top bottom' → 'top top' maps cleanly onto the
    // Scene's natural scroll-off: the fade begins only AFTER the pin (dwell included)
    // releases — image fully arrived — and completes exactly as the Interlude fills the
    // screen. No overlap with the entrance, no trailing gap. The frame fades into the
    // next section's colour (raised via `fade`), which matches the Interlude.
    var evap = gsap.timeline({
      scrollTrigger: {
        trigger: nextEl || section,
        start: nextEl ? 'top bottom' : 'bottom bottom',
        // End at the Interlude's CENTRE, not its top. The Interlude's content is
        // vertically centred, so it only enters the viewport once the section has
        // risen ~halfway — ending the fade here means the image is fully gone right
        // as that content begins to appear: image completely dissolved BEFORE the
        // next section shows, with no empty gold gap after it (content arrives as the
        // image leaves). 'top top' let the image linger through the whole rise;
        // pulling it earlier than centre would re-open a gap before the content.
        end: nextEl ? 'top center' : 'bottom top',
        scrub: 1,
        invalidateOnRefresh: true,
        // Mobile only: feather the image's bottom edge (CSS .is-evaporating) for the
        // duration of the dissolve, so its crisp rectangular edge doesn't scroll off as
        // a hard line against the Interlude (desktop softens this with the blur/drift
        // the mobile fade omits). Added when entering the dissolve from either
        // direction, removed on scrolling back up into the pinned full-screen image.
        onEnter: featherOn,
        onEnterBack: featherOn,
        onLeaveBack: featherOff,
        // Measure AFTER the Scene pin's spacer is applied (same reason the pin uses -1).
        refreshPriority: -1
      }
    });
    tweens.push(evap);
    if (evap.scrollTrigger) triggers.push(evap.scrollTrigger);

    // Explicit `from` (immediateRender:false) so the evaporate owns its composed start
    // (opacity 1, the scale the step-through left it, sharp) and a refresh/fast-scroll
    // can't start the fade from a stale value. Duration 1 @ pos 0 = spans the full
    // scroll-off. Desktop: scale-up + soft blur + upward drift (mist rising). Mobile:
    // a straight opacity cross-fade — the natural scroll-off already carries the image
    // up, and the rising gold Interlude fills behind it, so no extra drift is added.
    if (allowBlur) {
      evap.fromTo(
        bg,
        { opacity: 1, scale: 1.08, y: 0, filter: 'blur(0px)' },
        { opacity: 0, scale: 1.14, y: -80, filter: 'blur(8px)', ease: 'none', duration: 1, immediateRender: false },
        0
      );
    } else {
      evap.fromTo(
        bg,
        { opacity: 1 },
        { opacity: 0, ease: 'none', duration: 1, immediateRender: false },
        0
      );
    }
    // Raise the next section's colour behind the image exactly as it leaves, so it
    // dissolves straight into that colour (not Scene's own).
    if (fade) evap.fromTo(fade, { opacity: 0 }, { opacity: 1, ease: 'none', duration: 1, immediateRender: false }, 0);

    // NOTE: no manual resize → ScrollTrigger.refresh() here. A bare refresh()
    // forces a global _refreshAll that BYPASSES the ignoreMobileResize guard set
    // in animations.js, so on iOS every address-bar show/hide would re-measure
    // this pin and snap the scrub — the image visibly blinking out and back.
    // ScrollTrigger's own built-in resize listener already refreshes on real
    // resizes / orientation changes and correctly ignores the address bar.
  }

  function initAll() {
    gsap.registerPlugin(ScrollTrigger);
    document.querySelectorAll('[data-scene]').forEach(setup);
  }

  whenReady(initAll);

  // Recalculate once images/fonts have settled. (The Intro Portal already calls
  // ScrollTrigger.refresh() when it builds its post-entry dissolve pin, which
  // recomputes this section's pin start — so no extra refresh is needed here.)
  window.addEventListener('load', function () {
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  });

  // Shopify theme editor: tear down + rebuild when the section reloads, so
  // changing a setting never leaves the sequence stuck at its hidden state.
  document.addEventListener('shopify:section:load', function (e) {
    if (!ready()) return;
    if (!e.target.querySelector('[data-scene]')) return;
    reset();
    document.querySelectorAll('[data-scene]').forEach(setup);
  });
})();
